/* eslint-disable security/detect-object-injection */
/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import { IWebgroupFiltersData } from './webgroup-filters.data';
import { IWebgroupFiltersProps, IWebgroupFiltersResources } from './webgroup-filters.props.autogenerated';
import { WebGroupFilter } from './components/webgroup-filter';
import { Drawer, Module, IModuleProps } from '@msdyn365-commerce-modules/utilities';
import _remove from 'lodash/remove';
import { ProductRefiner, ProductRefinerValue } from '@msdyn365-commerce/retail-proxy';
import { FiltersSortOrder, SortOrder } from '../../actions/get-filters-sorting-data.action';
import { normalizeFilterSortKey } from './components/filter-sort-normalizer';
//import { filter } from 'lodash';

export interface IWebgroupFiltersState {
    isDrawerOpen: boolean;
    drawerListId: number | undefined;
    openDrawerList: number[];
    filters: ProductRefiner[];
    accordions: JSX.Element[];
    loaded: boolean;
    selectedFilters: IWebgroupFilter[];
    sortOrder: FiltersSortOrder[] | [];
    resources: IWebgroupFiltersResources;
}

export interface IWebgroupFilter {
    FilterName: string;
    FilterValues: string[];
}
export interface IWebGroupFilterGroup {
    name: string;
    filters: ProductRefiner[];
    filterChange: any;
}

export interface IWebgroupFiltersExtendedProps extends IWebgroupFiltersProps<IWebgroupFiltersData> {
    accordions: (JSX.Element | null)[];
    clearFilters: () => void;
}

/**
 *
 * WebgroupFilters component
 * @extends {React.PureComponent<IWebgroupFiltersProps<IWebgroupFiltersData>>}
 */
class WebgroupFilters extends React.PureComponent<IWebgroupFiltersExtendedProps, IWebgroupFiltersState> {
    public constructor(props: IWebgroupFiltersExtendedProps) {
        super(props);
        this.state = {
            openDrawerList: [],
            drawerListId: undefined,
            isDrawerOpen: false,
            selectedFilters: [],
            accordions: [],
            filters: [],
            loaded: false,
            sortOrder: [],
            resources: {} as IWebgroupFiltersResources
        };
    }
    private _renderCellData(group: any): JSX.Element | null {
        return <WebGroupFilter name={group.KeyName} filters={group.Values} filterChange={this._changeFitler} key={group.RecordId} />;
    }
    private readonly _toggle = (drawerListId: number | undefined) => (): void => {
        if (drawerListId) {
            if (this.state.openDrawerList.length) {
                let drawerList = this.state.openDrawerList.slice(0);
                if (this.state.openDrawerList.indexOf(drawerListId) > -1) {
                    drawerList = drawerList.filter(v => v !== drawerListId);
                } else {
                    drawerList.push(drawerListId);
                }
                this.setState({ openDrawerList: drawerList });
            } else {
                this.setState({ openDrawerList: [drawerListId] });
            }
        }

        if (this.state.drawerListId !== drawerListId) {
            this.setState({ drawerListId, isDrawerOpen: true });
        } else {
            this.setState({ drawerListId: undefined, isDrawerOpen: false });
        }
    };
    private _createDrawerBody(): void {
        const tempOpenDrawerList: number[] = [];
        let accordion: any = [];
        this._sortFilters();
        if (this.state.filters?.length) {
            accordion = this.state.filters.map((group, index: number) => {
                if (group.Values?.length) {
                    const cellData: JSX.Element | null = this._renderCellData(group);
                    tempOpenDrawerList.push(group.RecordId!);
                    const accordionItemContainer: IModuleProps = {
                        moduleProps: this.props,
                        className: 'ms-filter-title'
                    };
                    return (
                        <Module {...accordionItemContainer} key={group.RecordId}>
                            <Drawer
                                key={index}
                                collapseProps={{
                                    timeout: 0,
                                    isOpen: this.state.openDrawerList.indexOf(group.RecordId ?? 0) > -1 ? true : false
                                }}
                                className='ms-filter__drawer'
                                openGlyph='ms-filter__drawer-open'
                                closeGlyph='ms-filter__drawer-close'
                                glyphPlacement='end'
                                toggleButtonText={group?.KeyName || ''}
                                onToggle={this._toggle(group.RecordId)}
                            >
                                <div className='ms-filter-details'>
                                    <span className='ms-filter-details'>{cellData}</span>
                                </div>
                            </Drawer>
                        </Module>
                    );
                }
                return null;
            });
        }
        this.setState({ accordions: accordion, loaded: true });
    }
    private readonly _changeFitler = (event: any, filter: ProductRefinerValue, name: string): void => {
        const value = filter.LeftValueBoundString || '';
        const fgroupg = this.state.selectedFilters.find(f => f.FilterName === name);

        if (event.currentTarget.checked) {
            if (fgroupg) {
                fgroupg.FilterValues.push(value);
            } else {
                this.state.selectedFilters.push({
                    FilterName: name,
                    FilterValues: [value]
                });
            }
        } else if (this.state.selectedFilters) {
            if (fgroupg?.FilterValues.length === 1) {
                _remove(this.state.selectedFilters as any, (f: IWebgroupFilter) => f.FilterName === name);
            } else {
                const index = fgroupg?.FilterValues.indexOf(value);
                // @ts-ignore: Object is possibly 'null'.
                if (index !== -1) {
                    // @ts-ignore: Object is possibly 'null'.
                    fgroupg?.FilterValues.splice(index, 1);
                }
            }
        }

        document.dispatchEvent(new CustomEvent('FILTERS_UPDATED', { detail: this.state.selectedFilters }));
    };

    private readonly _clearFilters = (): void => {
        this.setState({ selectedFilters: [] });
        document.dispatchEvent(new CustomEvent('FILTERS_UPDATED', { detail: [] }));
        const filterInputs = document.getElementsByClassName('filter-checkbox');

        for (let i = 0; i < filterInputs.length; i++) {
            (filterInputs[i] as HTMLInputElement).checked = false;
        }
    };

    private readonly quickSort = (arr: SortOrder[]): SortOrder[] => {
        if (arr.length <= 1) {
            return arr;
        }

        const pivot = arr[arr.length - 1];
        const leftArr = [];
        const rightArr = [];

        for (let i = 0; i < arr.length - 1; i++) {
            if (this.compareSortOrders(arr[i], pivot)) {
                rightArr.push(arr[i]);
            } else {
                leftArr.push(arr[i]);
            }
        }

        return [...this.quickSort(leftArr), pivot, ...this.quickSort(rightArr)];
    };

    // private readonly shuffle = (array: SortOrder[]) => {
    //     for (let i = array.length - 1; i > 0; i--) {
    //         const j = Math.floor(Math.random() * (i + 1));
    //         [array[i], array[j]] = [array[j], array[i]];
    //     }
    //     return array;
    // };

    private readonly compareSortOrders = (a: SortOrder, b: SortOrder): boolean => {
        const numA: number = parseFloat(a.SortOrder);
        const numB: number = parseFloat(b.SortOrder);
        if (numA === numB) {
            const charA = a.SortOrder.charAt(a.SortOrder.length - 1);
            const charB = b.SortOrder.charAt(b.SortOrder.length - 1);

            return charA > charB;
        } else {
            return numA > numB;
        }
    };

    private readonly _normalizeName = (value?: string): string => {
        return (value || '').trim().toLowerCase();
    };

    private readonly _getSortGroupName = (keyName?: string): keyof FiltersSortOrder => {
        const normalized = this._normalizeName(keyName);

        const imperialNames = new Set([
            this._normalizeName(this.state.resources.diamImperial),
            'screw size (imperial)',
            'diam/thread size (imperial)'
        ]);

        const metricNames = new Set([
            this._normalizeName(this.state.resources.diamMetric),
            'screw size (metric)',
            'diam/thread size (metric)'
        ]);

        if (imperialNames.has(normalized)) {
            return 'Diam/Thread Size (imperial)';
        }

        if (metricNames.has(normalized)) {
            return 'Diam/Thread Size (metric)';
        }

        return 'Size/Length & Diameter';
    };

    private readonly _getSortListForFilter = (keyName?: string, sortGroupName?: keyof FiltersSortOrder): SortOrder[] => {
        const normalized = this._normalizeName(keyName);

        if (normalized === 'screw size (imperial)') {
            // @ts-ignore
            const sizeList = (this.state.sortOrder['Size/Length & Diameter'] as SortOrder[]) || [];
            // @ts-ignore
            const imperialThreadList = (this.state.sortOrder['Diam/Thread Size (imperial)'] as SortOrder[]) || [];

            return [...sizeList, ...imperialThreadList];
        }

        // @ts-ignore
        return (this.state.sortOrder[sortGroupName || 'Size/Length & Diameter'] as SortOrder[]) || [];
    };

    private readonly _sortFilters = (): void => {
        const filters = [...this.state.filters].sort((a: any, b: any) => a.KeyName.localeCompare(b.KeyName));

        filters.forEach(f => {
            const sortGroupName = this._getSortGroupName(f.KeyName);
            const filterSortList = this._getSortListForFilter(f.KeyName, sortGroupName);
            const newFilterSortList = this.quickSort([...filterSortList]);

            const sortIndexByValue = new Map<string, number>();

            newFilterSortList.forEach((item, index) => {
                const key = (item.Value || '').trim().toLowerCase();
                if (key && !sortIndexByValue.has(key)) {
                    sortIndexByValue.set(key, index);
                }
            });

            if (f.Values?.length) {
                const fallbackStart = newFilterSortList.length;

                f.Values.forEach((fv: any, index: number) => {
                    const raw = fv.LeftValueBoundString || '';

                    const candidateKeys = [
                        raw.trim().toLowerCase(),
                        normalizeFilterSortKey(raw, false),
                        normalizeFilterSortKey(raw, true)
                    ].filter(Boolean);

                    const matchedIndex = candidateKeys.map(candidate => sortIndexByValue.get(candidate)).find(value => value !== undefined);

                    fv.RowNumber = matchedIndex ?? fallbackStart + index;
                });

                f.Values = [...f.Values].sort((a: any, b: any) => (a.RowNumber ?? 0) - (b.RowNumber ?? 0));
            }
        });

        this.setState({ filters });
    };

    public render(): JSX.Element | null {
        const {
            data: { filters, filtersSortOrder },
            resources
        } = this.props;
        if (!this.state.loaded && filters?.result) {
            this.setState(
                {
                    filters: filters.result,
                    sortOrder: filtersSortOrder?.result?.FiltersSortOrder || [],
                    resources: resources
                },
                this._createDrawerBody
            );
        }
        const viewProps = {
            ...this.props,
            accordions: this.state.accordions,
            clearFilters: this._clearFilters
        };
        return this.props.renderView(viewProps);
    }
}

export default WebgroupFilters;
